import { useState } from 'react';
import { Tabs as TabsComponent, Typography, Wrapper } from '@screentone/core';
import { v4 as uuidv4 } from 'uuid';

import PageDraftTrash from 'components/page-draft-trash/PageDraftTrash';
import { PublishHistory } from 'components/publish-history/PublishHistory';
import { QueryRulesForm } from 'components/query-rules-form/QueryRulesForm';
import { EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  ArticleAttributes,
  AvailableFeatureFlags,
  CollectionDto,
  ListContainer,
  QueryItem
} from 'data/generated/graphql';
import {
  AllessehContentQueryBody,
  AllessehQueryRule,
  AllessehQuerySortKeyTypes,
  AllessehQuerySortOrderType
} from 'hooks/useAllessehContentQuery';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { addExcludedContentToQuery } from 'utils/collection';
import { SearchContent, SearchProps } from '../../../../components/search-content/SearchContent';

interface CollectionCurateSectionProps {
  searchProps: SearchProps;
  queryProps: {
    allessehJsonQuery?: string | null;
    queryItemIndex: number;
    totalItemsLength: number;
  };
  isReadOnly?: boolean;
  liveVersion?: CollectionDto;
  hasRecentlyPublished?: boolean;
}

enum Tabs {
  ContentSearch = 0,
  Query = 1,
  Embargoed = 2,
  PublishHistory = 3
}

const PUBLISH_HISTORY_PAGE_SIZE = 10;

export const CollectionCurateSection = ({
  searchProps,
  queryProps,
  isReadOnly = false,
  liveVersion,
  hasRecentlyPublished = false
}: CollectionCurateSectionProps) => {
  const [tabValue, setTabValue] = useState(Tabs.ContentSearch);
  const { insertEntity, modifyEntity, removeEntity, root } = useDataModelContext();
  const showQueryTab = useFeatureFlag(AvailableFeatureFlags.ShowQueryTabInSingleCollectionView, true);
  const showEmbargoedTab = useFeatureFlag(AvailableFeatureFlags.ShowEmbargoedCollections, true);

  const getExcludedContent = (query: string) => {
    let jsonQueryStr = query;
    const rootListContainer = root as ListContainer;

    rootListContainer.collection.forEach((item) => {
      if (item.type === EntityType.Article) {
        const articleAttributes = item.attributes as ArticleAttributes;
        const parsedJsonQuery = addExcludedContentToQuery(articleAttributes.id, jsonQueryStr);
        jsonQueryStr = JSON.stringify(parsedJsonQuery);
      }
    });

    return jsonQueryStr;
  };

  const handleAllessehJsonQueryChange = (newAllessehJsonQuery: string, addExcludedContent?: boolean) => {
    let jsonQueryStr = newAllessehJsonQuery;
    if (queryProps.queryItemIndex >= 0) {
      const hierarchyId = `0-${queryProps.queryItemIndex}`;

      if (addExcludedContent && root.type === EntityType.List) {
        jsonQueryStr = getExcludedContent(jsonQueryStr);
      }
      if (jsonQueryStr) {
        modifyEntity(hierarchyId, (entity) => {
          const query = entity as QueryItem;
          query.attributes.query = JSON.parse(jsonQueryStr) as Record<string, unknown>;
        });
      } else {
        removeEntity(hierarchyId);
      }
    } else {
      const hierarchyId = `0-${queryProps.totalItemsLength}`;
      const newQueryItem: QueryItem = {
        type: EntityType.Query,
        uuid: uuidv4(),
        attributes: {
          id: uuidv4(),
          query: JSON.parse(jsonQueryStr) as Record<string, unknown>
        }
      };

      insertEntity(hierarchyId, newQueryItem);
    }
  };

  const handleQueryRuleChange = (newAllessehJsonQuery: string) => {
    let addExcludedContent = false;
    const allessehJsonQueryParsed = JSON.parse(queryProps.allessehJsonQuery ?? '') as AllessehContentQueryBody;
    const newAllessehJsonQueryParsed = JSON.parse(newAllessehJsonQuery) as AllessehContentQueryBody;

    if (newAllessehJsonQueryParsed.query?.and?.length) {
      const allessehQueryRule =
        allessehJsonQueryParsed.query?.and?.some((item: AllessehQueryRule) => 'term' in item && !!item.term.value) ??
        false;
      const newAllessehQueryRule = newAllessehJsonQueryParsed.query.and.some(
        (item: AllessehQueryRule) => 'term' in item && !!item.term.value
      );

      addExcludedContent = newAllessehQueryRule && !allessehQueryRule;
    }

    handleAllessehJsonQueryChange(newAllessehJsonQuery, addExcludedContent);
  };

  return (
    <>
      <TabsComponent
        data-testid="collection-curate-section-tabs"
        role="tablist"
        onChange={setTabValue}
        value={tabValue}
      >
        <TabsComponent.Item
          data-testid="collection-curate-section-tab-content-search"
          role="tab"
          id="tab-id0"
          value={Tabs.ContentSearch}
          aria-selected={tabValue === Tabs.ContentSearch ? 'true' : 'false'}
          aria-controls="tabpanel-id0"
        >
          Content Search
        </TabsComponent.Item>
        {!isReadOnly && showQueryTab && (
          <TabsComponent.Item
            data-testid="collection-curate-section-tab-query"
            role="tab"
            id="tab-id1"
            value={Tabs.Query}
            aria-selected={tabValue === Tabs.Query ? 'true' : 'false'}
            aria-controls="tabpanel-id1"
          >
            Query
          </TabsComponent.Item>
        )}
        {showEmbargoedTab && (
          <TabsComponent.Item
            role="tab"
            id="tab-id2"
            value={Tabs.Embargoed}
            aria-selected={tabValue === Tabs.Embargoed ? 'true' : 'false'}
            aria-controls="tabpanel-id2"
          >
            Embargoed
          </TabsComponent.Item>
        )}
        <TabsComponent.Item
          data-testid="collection-curate-section-tab-publish-history"
          role="tab"
          id="tab-id3"
          value={Tabs.PublishHistory}
          aria-selected={tabValue === Tabs.PublishHistory ? 'true' : 'false'}
          aria-controls="tabpanel-id3"
        >
          Publish History
        </TabsComponent.Item>
      </TabsComponent>
      {tabValue === Tabs.ContentSearch && (
        <Wrapper
          data-testid="collection-curate-section-collections-container"
          id="tabpanel-id0"
          role="tabpanel"
          aria-labelledby="tab-id0"
          padding={{ top: 'sm' }}
        >
          <SearchContent
            searchProps={searchProps}
            beforeContent={
              <PageDraftTrash
                messageLabel="collection"
                // Temp fix. Will be refactored once CollectionCurateSection is redesigned
                overrideStyle={{
                  paddingRight: 'var(--st-spacer-lg)',
                  marginLeft: 'calc(-1 * var(--st-spacer-lg))'
                }}
              />
            }
          />
        </Wrapper>
      )}
      {tabValue === Tabs.Query && !isReadOnly && (
        <Wrapper
          data-testid="collection-curate-section-query-container"
          id="tabpanel-id1"
          role="tabpanel"
          aria-labelledby="tab-id1"
          padding={{ top: 'sm' }}
        >
          <QueryRulesForm
            allessehJsonQuery={queryProps.allessehJsonQuery}
            onAllessehJsonQueryChange={(newAllessehJsonQuery: string) =>
              handleAllessehJsonQueryChange(newAllessehJsonQuery, false)
            }
            onQueryRuleChange={handleQueryRuleChange}
          />
        </Wrapper>
      )}
      {tabValue === Tabs.Embargoed && (
        <Wrapper id="tabpanel-id2" role="tabpanel" aria-labelledby="tab-id2" padding={{ top: 'sm' }}>
          <Typography
            variant="header3"
            margin={{ bottom: 'none', top: 'md' }}
            data-testid="page-multi-tab-section-embargoed-tab-title"
          >
            Embargoed
          </Typography>
          <SearchContent
            searchProps={{
              ...searchProps,
              searchParams: {
                ...searchProps.searchParams,
                filters: {
                  ...searchProps.searchParams.filters,
                  author: searchProps.defaultFilterState.author,
                  publishDates: searchProps.defaultFilterState.publishDates,
                  contentTypes: searchProps.defaultFilterState.contentTypes,
                  embargo: true
                },
                sort: AllessehQuerySortKeyTypes.EmbargoDate,
                sortOrder: AllessehQuerySortOrderType.ASC
              }
            }}
            beforeContent={<PageDraftTrash messageLabel="collection" />}
            embargo
          />
        </Wrapper>
      )}
      {tabValue === Tabs.PublishHistory && (
        <Wrapper id="tabpanel-id3" role="tabpanel" aria-labelledby="tab-id3" padding={{ top: 'sm' }}>
          <PublishHistory<CollectionDto>
            liveVersion={liveVersion}
            publishUtc={0}
            hasRecentlyPublished={hasRecentlyPublished}
            isReadOnly={isReadOnly}
            hidePreview
            initialPageSize={PUBLISH_HISTORY_PAGE_SIZE}
          />
        </Wrapper>
      )}
    </>
  );
};
